import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty, debounce } from 'lodash'
import queryString from 'query-string'
import pluralize from 'pluralize'
import { IconSparkles } from '@tabler/icons-react'
import { FilterForm } from '@/components/filters/FilterForm'

import { Loader } from '@/components/loaders/Loader'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import { toast } from '@/components/toasts/toast.helpers'
import { formatTime } from '@/utils/helpers'
import {
  INBOX_DEBOUNCE_MS,
  PROMPT_ENGINEER_INBOX,
  COMPLETED,
  EXPIRED,
  IGNORED,
  READY_FOR_REVIEW,
  IN_PROGRESS,
  COLLECTING_EVIDENCE,
  TASK_STATUS_MAP,
} from '@/reducers/tasks/tasks.constants'
import { fetchTaskById, patchTask } from '@/reducers/tasks/tasks.actions'
import { setTask } from '@/reducers/tasks/tasks.redux'
import {
  fetchPromptFeedbackTasks,
  fetchFeedbackOptimizations,
  requestCalibration,
} from '@/reducers/promptFeedback/promptFeedback.actions'
import { setPagination, CALIBRATION_POLL_MS } from '@/reducers/promptFeedback/promptFeedback.redux'
import { POLLABLE_STATUSES } from '@/reducers/promptimizer/promptimizer.redux'
import { fetchOrganizations } from '@/reducers/organizations/organizations.actions'
import { fetchScorecardsByOrg } from '@/reducers/scorecards/scorecards.actions'
import { selectSupportWindowOpenForOrg } from '@/reducers/supportAccess/supportAccess.redux'
import { PROMPT_ENGINEERING_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'
import { fetchSavedFilters } from '@/reducers/savedFilters/savedFilters.actions'
import { AdvancedTablePagination } from '@/components/tables/AdvancedTable/AdvancedTablePagination'
import { InboxZero } from '@/components/inbox/components/InboxZero'
import { Menu } from '@/components/navigation/Menu'
import { TODO, ARCHIVE, getSortByOptions, taskTypeOptions } from '@/components/inbox/inbox.helpers'
import { FilterTypeSort } from '@/components/filters/types/FilterTypeSort'
import { FilterTypeDropdown } from '@/components/filters/types/FilterTypeDropdown'
import { FilterTypeMultiSelect } from '@/components/filters/types/FilterTypeMultiSelect'
import { PageHeader } from '@/components/headers/PageHeader'
import { SavedFilters } from '@/components/savedFilters/SavedFilters'
import { Drawer } from '@/components/drawer/Drawer'
import { CopyButton } from '@/components/buttons/CopyButton'
import Select from '@/components/forms/Select'
import { PromptFeedbackFilters } from '@/views/PromptEngineering/components/PromptFeedbackFilters'
import { PromptFeedbackCard } from '@/views/PromptEngineering/components/PromptFeedbackCard'
import { PromptFeedbackTask } from '@/views/PromptEngineering/components/PromptFeedbackTask'
import { CustomerFeedbackDrawer } from '@/components/inbox/drawers/customer-feedback/CustomerFeedbackDrawer'
import { FormField } from '@/components/forms/FormField'
import { AGENT_TAB_ID, CalibrationAgentPanel, TASK_NAME_PREFIX } from './CalibrationAgentPanel'

import '@/components/inbox/Inbox.scss'
import '@/views/CustomerFeedback/CustomerFeedbackInbox.scss'

// Default date range: last 60 days (matches other inboxes)
const getDefaultStartDate = () => {
  const date = new Date()
  date.setDate(date.getDate() - 60)
  return formatTime(date, 'yyyy-MM-DDT00:00:00Z')
}
const getDefaultEndDate = () => formatTime(new Date(), 'yyyy-MM-DDT23:59:59Z')

export const CalibrationInbox = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const {
    tasks: promptFeedbackTasks = [],
    loading: promptFeedbackLoading,
    pagination,
    counts,
    optimizations: { byTaskId: optimizationsByTaskId },
  } = useSelector((state) => state.promptFeedback)
  const { task } = useSelector((state) => state.tasks)
  const organizations = useSelector((state) => state.organizations) || []
  const { scorecardConfigs = [] } = useSelector((state) => state.scorecards.data)
  const { organizationid: currentUserOrgId, prompt_editor: isPromptEditor } = useSelector(
    (state) => state.currentUser
  )
  const { savedFilterList } = useSelector((state) => state.savedFilters)
  // QCER-802: read the support-access slice once so we can check it per-task
  // below. The window must belong to the task's org (the slice's windowOrgId),
  // otherwise an Org A window would un-protect Org B tasks in the same inbox.
  const supportAccess = useSelector((state) => state.supportAccess)
  const [calibratingTaskIds, setCalibratingTaskIds] = useState([])

  // Only Org 1 (Balto admin) can view all orgs; others are restricted to their own org
  const isBaltoAdmin = currentUserOrgId === 1
  const canEditPrompts = isBaltoAdmin || isPromptEditor

  // Filter state - arrays for multi-select, non-Balto users default to their own org
  const [selectedOrgIds, setSelectedOrgIds] = useState(
    isBaltoAdmin ? [] : [{ value: currentUserOrgId, label: '' }]
  )
  const [selectedScorecardIds, setSelectedScorecardIds] = useState([])
  const [selectedCustomerScorecardId, setSelectedCustomerScorecardId] = useState(null)
  const [startDate, setStartDate] = useState(getDefaultStartDate)
  const [endDate, setEndDate] = useState(getDefaultEndDate)

  const [sortConfig, setSortConfig] = useState({ sortBy: 'feedback_count', orderBy: 'desc' })
  const [openTab, setOpenTab] = useState(TODO)
  const [taskStatuses, setTaskStatuses] = useState([])
  const [minReports, setMinReports] = useState('')
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState(null)

  const { search } = location
  const isArchive = openTab === ARCHIVE
  const inboxType = PROMPT_ENGINEER_INBOX
  const taskUrl = `${window.origin}${location.pathname}${location.search}`

  const todoCount = (counts.inbox || 0) + (counts.notReady || 0)

  const currentlyAppliedFilter = savedFilterList[PROMPT_ENGINEERING_FILTER_TYPE]?.find(
    (filter) => filter.uuid === selectedSavedFilterId
  )

  const orgLookup = useMemo(
    () => new Map(organizations.map((org) => [org.id, org])),
    [organizations]
  )

  const orgOptions = organizations
    .map((org) => ({ value: org.id, label: org.name }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const scorecardOptions = scorecardConfigs
    .map((config) => ({ value: config.id, label: config.name }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const handleDateRangeChange = (range) => {
    setStartDate(formatTime(range.selection.startDate, 'yyyy-MM-DDT00:00:00Z'))
    setEndDate(formatTime(range.selection.endDate, 'yyyy-MM-DDT23:59:59Z'))
  }

  const handleOrgChange = (options) => {
    setSelectedOrgIds(options || [])
    setSelectedScorecardIds([])
  }

  const handleScorecardChange = (options) => {
    setSelectedScorecardIds(options || [])
  }

  const handleCustomerScorecardChange = (option) => {
    setSelectedCustomerScorecardId(option?.value || null)
  }

  const handleSelectSavedFilter = (filterUuid) => {
    setSelectedSavedFilterId(filterUuid || null)
  }

  const openStatusOptions = [
    { value: READY_FOR_REVIEW, label: TASK_STATUS_MAP[READY_FOR_REVIEW] },
    { value: COLLECTING_EVIDENCE, label: TASK_STATUS_MAP[COLLECTING_EVIDENCE] },
    { value: IN_PROGRESS, label: TASK_STATUS_MAP[IN_PROGRESS] },
  ]

  const getTaskStatusesFilter = () => {
    const tabStatuses = isArchive
      ? [COMPLETED, EXPIRED, IGNORED]
      : [READY_FOR_REVIEW, IN_PROGRESS, COLLECTING_EVIDENCE]
    const selected = taskStatuses.filter((status) => tabStatuses.includes(status))
    return isEmpty(selected) ? tabStatuses : selected
  }

  const getScorecardIdsParam = () => {
    if (isBaltoAdmin) {
      return !isEmpty(selectedScorecardIds)
        ? selectedScorecardIds.map((scorecard) => scorecard.value)
        : undefined
    }
    return selectedCustomerScorecardId ? [selectedCustomerScorecardId] : undefined
  }

  const fetchTasksForInbox = (page = 1) => {
    return dispatch(
      fetchPromptFeedbackTasks({
        page,
        limit: pagination.limit,
        sortBy: sortConfig.sortBy === 'feedback_count' ? 'evidence_count' : sortConfig.sortBy,
        sortOrder: sortConfig.orderBy,
        organizationId: !isEmpty(selectedOrgIds)
          ? selectedOrgIds.map((org) => org.value)
          : undefined,
        scorecardConfigId: getScorecardIdsParam(),
        minReports: minReports || null,
        startDate,
        endDate,
        taskStatuses: getTaskStatusesFilter(),
      })
    )
  }

  // Ref to hold latest fetchTasksForInbox for debounced calls
  const fetchRef = useRef(fetchTasksForInbox)
  fetchRef.current = fetchTasksForInbox

  const debouncedFetch = useRef(
    debounce((page) => {
      fetchRef.current(page)
    }, INBOX_DEBOUNCE_MS)
  ).current

  const handlePageChange = (newPage) => {
    const resolvedPage = typeof newPage === 'function' ? newPage(pagination.page) : newPage
    dispatch(setPagination({ page: resolvedPage }))
    fetchTasksForInbox(resolvedPage)
  }

  const handleUpdateTask = async (taskId, organizationId, status, handleClose) => {
    await dispatch(patchTask(taskId, { status }, inboxType, organizationId))
    await fetchTasksForInbox(pagination.page)
    if (handleClose) {
      handleClose()
    }
    if (status === IGNORED) {
      toast.success('Task dismissed')
    }
  }

  const handleOpenTaskDrawer = (selectedTask) => {
    dispatch(setTask(selectedTask))
    history.push({
      search: queryString.stringify({ id: selectedTask.id, org_id: selectedTask.organization_id }),
    })
  }

  const handleCloseTaskDrawer = () => {
    dispatch(setTask(null))
    history.push({})
  }

  const inboxes = [
    { id: TODO, title: 'Inbox', primaryAction: 'View' },
    { id: ARCHIVE, title: 'Archive', primaryAction: 'View' },
    ...(canEditPrompts
      ? [
          {
            id: AGENT_TAB_ID,
            title: 'Agent',
            primaryAction: 'View',
            icon: <IconSparkles className="status-magical icon-svg" />,
          },
        ]
      : []),
  ]

  const currentTab = inboxes.find((inbox) => inbox.id === openTab)

  useEffect(() => {
    const params = queryString.parse(search)
    if (params.id) {
      // Prompt editors get hidden evidence back so the drawer can render its Dismissed view;
      // read-only viewers keep the pending-only payload.
      dispatch(
        fetchTaskById(
          params.id,
          handleOpenTaskDrawer,
          handleCloseTaskDrawer,
          params.org_id,
          canEditPrompts
        )
      )
    }
  }, [search])

  const isInitialMount = useRef(true)

  useEffect(() => {
    fetchTasksForInbox(1)
    if (isBaltoAdmin) {
      dispatch(fetchOrganizations())
      dispatch(fetchSavedFilters(PROMPT_ENGINEERING_FILTER_TYPE))
    }
  }, [])

  // Refetch when filters change (debounced) — skip initial mount to avoid duplicate fetch
  useEffect(() => {
    if (!isInitialMount.current) {
      debouncedFetch(1)
    }
    return () => debouncedFetch.cancel()
  }, [
    sortConfig,
    selectedOrgIds,
    selectedScorecardIds,
    selectedCustomerScorecardId,
    minReports,
    startDate,
    endDate,
    taskStatuses,
  ])

  // Refetch immediately when tab changes — skip initial mount to avoid duplicate fetch. The agent
  // tab has no task list; its panel fetches its own agenda.
  useEffect(() => {
    if (!isInitialMount.current && openTab !== AGENT_TAB_ID) {
      fetchTasksForInbox(1)
    }
    isInitialMount.current = false
  }, [openTab])

  useEffect(() => {
    if (!isEmpty(selectedOrgIds)) {
      // Only fetches for the first selected org — multi-org scorecard merging not yet supported
      dispatch(fetchScorecardsByOrg(selectedOrgIds[0].value, 'copilot'))
    }
  }, [selectedOrgIds])

  useEffect(() => {
    if (canEditPrompts && !isEmpty(promptFeedbackTasks)) {
      dispatch(
        fetchFeedbackOptimizations(promptFeedbackTasks.map((feedbackTask) => feedbackTask.id))
      )
    }
  }, [promptFeedbackTasks])

  const activeTaskIds = promptFeedbackTasks
    .map((feedbackTask) => feedbackTask.id)
    .filter((taskId) => POLLABLE_STATUSES.includes(optimizationsByTaskId[taskId]?.status))
  const activeTaskKey = activeTaskIds.join(',')
  useEffect(() => {
    if (!canEditPrompts || isEmpty(activeTaskIds)) {
      return undefined
    }
    const timer = setInterval(
      () => dispatch(fetchFeedbackOptimizations(activeTaskIds)),
      CALIBRATION_POLL_MS
    )
    return () => clearInterval(timer)
  }, [activeTaskKey])

  const handleCalibrateTask = async (taskId) => {
    setCalibratingTaskIds((ids) => [...ids, taskId])
    await dispatch(requestCalibration(taskId))
    setCalibratingTaskIds((ids) => ids.filter((id) => id !== taskId))
  }

  useEffect(() => {
    if (currentlyAppliedFilter && currentlyAppliedFilter.filters) {
      const { organizations: filterOrgs, scorecards } = currentlyAppliedFilter.filters
      setSelectedOrgIds(filterOrgs || [])
      setSelectedScorecardIds(scorecards || [])
    }
  }, [currentlyAppliedFilter])

  const renderFilters = () => {
    if (isBaltoAdmin) {
      return (
        <PromptFeedbackFilters
          startDate={startDate}
          endDate={endDate}
          onDateRangeChange={handleDateRangeChange}
          selectedOrgIds={selectedOrgIds}
          onOrgChange={handleOrgChange}
          orgOptions={orgOptions}
          selectedScorecardIds={selectedScorecardIds}
          onScorecardChange={handleScorecardChange}
          scorecardOptions={scorecardOptions}
          showOrgFilter={isBaltoAdmin}
        />
      )
    }

    return (
      <FilterForm small className="customer-feedback-filter-form">
        <FormField>
          <label>Date Range</label>
          <DateRangePickerReusable
            data-testid="customer-date-range-picker"
            onChange={handleDateRangeChange}
            startDate={startDate}
            endDate={endDate}
          />
        </FormField>
        <FormField data-testid="scorecard-dropdown">
          <label>Scorecard</label>
          <Select
            isClearable
            placeholder="All Scorecards"
            value={selectedCustomerScorecardId}
            onChange={handleCustomerScorecardChange}
            options={scorecardOptions}
            fixedWidth
            data-testid="scorecard-filter"
          />
        </FormField>
        <FormField data-testid="status-dropdown">
          <label>Status</label>
          <Select
            isMulti
            isClearable
            placeholder="All Statuses"
            value={(isArchive ? taskTypeOptions : openStatusOptions).filter((option) =>
              taskStatuses.includes(option.value)
            )}
            onChange={(options) => setTaskStatuses((options || []).map((option) => option.value))}
            options={isArchive ? taskTypeOptions : openStatusOptions}
            fixedWidth
            data-testid="status-filter"
          />
        </FormField>
      </FilterForm>
    )
  }

  const renderSortFilterBar = () => {
    if (!isBaltoAdmin) {
      return null
    }

    return (
      <div className="flex-align-start medium-gap">
        <FilterTypeSort
          label="Sort By"
          options={getSortByOptions(inboxType)}
          sortConfig={sortConfig}
          updateSortConfig={setSortConfig}
          data-testid="inbox-sort-by"
        />
        <FilterTypeMultiSelect
          schema={{
            accessor: 'status',
            label: 'Status',
            options: isArchive ? taskTypeOptions : openStatusOptions,
          }}
          value={(isArchive ? taskTypeOptions : openStatusOptions).filter((option) =>
            taskStatuses.includes(option.value)
          )}
          updateSearch={(accessor, selected) =>
            setTaskStatuses((selected || []).map((option) => option.value))
          }
        />
        <FilterTypeDropdown
          schema={{
            accessor: 'minReports',
            label: 'Report Count',
            options: [
              { value: '1', label: '1 or more' },
              { value: '5', label: '5 or more' },
              { value: '10', label: '10 or more' },
            ],
          }}
          value={minReports}
          updateSearch={(accessor, updatedValue) => {
            setMinReports(updatedValue)
          }}
          showAllOnEmpty
          showAllOnEmptyLabel="All"
          isClearable={!!minReports}
        />
      </div>
    )
  }

  const renderDrawer = () => {
    if (canEditPrompts) {
      return (
        <PromptFeedbackTask
          handleUpdateTask={handleUpdateTask}
          refetchTasks={() => fetchTasksForInbox(pagination.page)}
        />
      )
    }

    return (
      <Drawer
        id={task?.id || 'empty-task'}
        extraWide
        title={
          <div className="flex-align-center medium-gap" data-testid="drawer-title">
            Calibration
            <CopyButton content={taskUrl} name="Link" />
          </div>
        }
      >
        {isEmpty(task) ? (
          <div>No task available.</div>
        ) : (
          <CustomerFeedbackDrawer refetchTasks={() => fetchTasksForInbox(pagination.page)} />
        )}
      </Drawer>
    )
  }

  return (
    <>
      <PageHeader
        decoration="ai"
        data-testid="inbox-title"
        title="Calibration"
        actions={
          isBaltoAdmin && (
            <SavedFilters
              filterType={PROMPT_ENGINEERING_FILTER_TYPE}
              noFiltersTitle="No Filters"
              noFiltersMessage="You have no saved filters"
              noFiltersButtonLabel="Create Filter"
              filterFormOptions={['name', 'organizations', 'scorecards', 'isDefault']}
              selectedSavedFilterId={selectedSavedFilterId}
              handleSelectSavedFilter={handleSelectSavedFilter}
            />
          )
        }
      />
      {renderFilters()}

      <div className={isBaltoAdmin ? 'prompt-feedback-content' : 'customer-feedback-content'}>
        <Menu
          items={inboxes.map((tab) => ({
            accessor: tab.id,
            label: tab.title,
            icon: tab.icon,
            active: openTab === tab.id,
            onClick: () => {
              setOpenTab(tab.id)
              if (tab.id !== AGENT_TAB_ID) {
                setTaskStatuses([])
              }
            },
            dataTestId: `tab-${tab.id}`,
            badge: tab.id === TODO ? todoCount : undefined,
          }))}
        />

        {openTab === AGENT_TAB_ID && (
          <CalibrationAgentPanel orgLookup={isBaltoAdmin ? orgLookup : undefined} />
        )}
        {openTab !== AGENT_TAB_ID && promptFeedbackLoading && isEmpty(promptFeedbackTasks) && (
          <div className="empty">
            <Loader data-testid="loader" />
          </div>
        )}
        {openTab !== AGENT_TAB_ID && !(promptFeedbackLoading && isEmpty(promptFeedbackTasks)) && (
          <div className="inbox-tasklist" data-testid="inbox-tasklist">
            {promptFeedbackLoading && (
              <div className="inbox-loading-overlay">
                <Loader data-testid="loader" />
              </div>
            )}
            {renderSortFilterBar()}
            {isEmpty(promptFeedbackTasks) ? (
              <InboxZero
                isArchive={isArchive}
                inboxCount={todoCount}
                archiveCount={counts.archive}
                inboxType={inboxType}
                currentTab={currentTab}
              />
            ) : (
              <div className="inbox-tasks" data-testid={`inbox-tasks-${currentTab.id}`}>
                {promptFeedbackTasks.map((feedbackTask) => {
                  const feedbackCount = feedbackTask.evidence?.length || 0
                  const uniqueCallCount =
                    new Set(feedbackTask.evidence?.map((evidence) => evidence.call_id)).size || 0
                  const taskOrg = orgLookup.get(feedbackTask.organization_id)
                  const orgName = feedbackTask.metadata?.organization_name || taskOrg?.name
                  // QCER-802: per-task window check — only un-protect this task
                  // when the slice's window belongs to its specific org.
                  const supportWindowActive =
                    isBaltoAdmin &&
                    selectSupportWindowOpenForOrg({ supportAccess }, feedbackTask.organization_id)
                  const isProtectedOrg =
                    taskOrg?.unredacted_enabled === true &&
                    currentUserOrgId !== feedbackTask.organization_id &&
                    !supportWindowActive
                  const taskOpen = ![COMPLETED, IGNORED, EXPIRED].includes(feedbackTask.status)
                  const promptName = feedbackTask.name?.startsWith(TASK_NAME_PREFIX)
                    ? feedbackTask.name.slice(TASK_NAME_PREFIX.length)
                    : feedbackTask.name || `Prompt ${feedbackTask.prompt_id}`

                  return (
                    <PromptFeedbackCard
                      key={feedbackTask.id}
                      dataTestId={`task-card-${feedbackTask.id}`}
                      title={promptName}
                      description={`${pluralize('report', feedbackCount, true)} from ${pluralize('call', uniqueCallCount, true)}`}
                      feedbackCount={feedbackCount}
                      createdDate={feedbackTask.created_at}
                      updatedDate={feedbackTask.updated_at}
                      status={feedbackTask.status}
                      buttonText={isBaltoAdmin && isProtectedOrg ? null : currentTab.primaryAction}
                      handleClick={() => handleOpenTaskDrawer(feedbackTask)}
                      callType={feedbackTask.evidence?.[0]?.call_type}
                      orgName={isBaltoAdmin ? orgName : undefined}
                      scorecardName={feedbackTask.evidence?.[0]?.scorecard_name}
                      isProtectedOrg={isBaltoAdmin ? isProtectedOrg : undefined}
                      optimization={
                        canEditPrompts ? optimizationsByTaskId[feedbackTask.id] : undefined
                      }
                      onCalibrate={
                        canEditPrompts &&
                        !isProtectedOrg &&
                        taskOpen &&
                        !POLLABLE_STATUSES.includes(optimizationsByTaskId[feedbackTask.id]?.status)
                          ? () => handleCalibrateTask(feedbackTask.id)
                          : undefined
                      }
                      calibrating={calibratingTaskIds.includes(feedbackTask.id)}
                    />
                  )
                })}
                <AdvancedTablePagination
                  activePage={pagination.page}
                  rowsPerPage={pagination.limit}
                  count={pagination.totalCount}
                  totalPages={pagination.totalPages}
                  setActivePage={handlePageChange}
                  transparent
                  label="tasks"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {renderDrawer()}
    </>
  )
}
